import React, { useState, useEffect, useRef } from 'react';
import {
  Heading,
  Box,
  Grid,
  SimpleGrid,
  Text,
  Fade,
  useBreakpointValue,
  List,
  ListItem,
  Collapse,
} from '@chakra-ui/react';
import {
  DAY_TIMELINE,
  GIORNATA_TIPO_CARDS,
  CIL,
  SECTION_TITLE_CONTAINER_BG,
  SECTION_TITLE_TEXT_COLOR,
} from './constants';
import ce1 from '../../images/ce-2025-1.jpg';
import ce2 from '../../images/ce-2025-2.jpg';
import ce3 from '../../images/ce-2025-3.jpg';
import ce4 from '../../images/ce-2025-4.jpg';
import backstageGiochiTranquilli from '../../images/ce-2026/Backstage.00_01_32_07.Immagine006.jpg';
import cantoImg from '../../images/ce-2026/canto.png';
import attivita11 from '../../images/ce-2026/att-2.png';
import attivita15 from '../../images/ce-2026/DSC08997.jpg';
import attivita930 from '../../images/ce-2026/IMG20250623112516.jpg';
import pranzoImg from '../../images/ce-2026/vlcsnap-2026-02-24-17h14m57s201.png';
import accoglienzaImg from '../../images/ce-2026/vlcsnap-2026-02-24-17h13m55s415.png';
import uscitaImg from '../../images/ce-2026/uscita.png';
import merenda1630Img from '../../images/ce-2026/merenda-2.png';

const DAY_IMAGES = [ce1, ce2, ce3, ce4];

const STEP_IMAGES = {
  accoglienza: accoglienzaImg,
  backstage: backstageGiochiTranquilli,
  attivita_930: attivita930,
  attivita_11: attivita11,
  attivita_15: attivita15,
  canto: cantoImg,
  pranzo: pranzoImg,
  merenda_1630: merenda1630Img,
  uscita: uscitaImg,
};

/** Tab sinistra/destra desktop: ListItem come pulsante, stile react bits */
const StepTab = ({ step, isSelected, onClick }) => (
  <ListItem listStyleType="none" mb={3}>
    <Box
      as="button"
      type="button"
      w="100%"
      textAlign="left"
      py={3}
      px={4}
      borderRadius="lg"
      borderWidth="1px"
      borderLeftWidth={isSelected ? '4px' : '1px'}
      borderColor={isSelected ? CIL.plum : 'rgba(59, 35, 90, 0.2)'}
      bg={isSelected ? CIL.plumLight : '#fffef9'}
      color={CIL.plum}
      fontWeight={isSelected ? '600' : '500'}
      boxShadow={isSelected ? 'md' : 'sm'}
      transition="all 0.2s ease"
      _hover={{
        borderColor: 'rgba(59, 35, 90, 0.35)',
        bg: isSelected ? CIL.plumLight : 'rgba(59, 35, 90, 0.06)',
        boxShadow: 'md',
      }}
      _focus={{
        outline: '2px solid',
        outlineColor: CIL.plum,
        outlineOffset: '2px',
      }}
      onClick={onClick}
    >
      <Text fontSize="sm" fontWeight="semibold" opacity={0.9} mb={0.5}>
        {step.time}
      </Text>
      <Text fontSize="md">{step.title}</Text>
    </Box>
  </ListItem>
);

/** Pannello destra desktop: dettaglio con immagine */
const DetailPanel = ({ step, imageIndex }) => {
  const panelImage = step.sectionImageKey
    ? STEP_IMAGES[step.sectionImageKey]
    : DAY_IMAGES[imageIndex % DAY_IMAGES.length];
  return (
    <Box
      bg="white"
      borderRadius="lg"
      overflow="hidden"
      boxShadow="0 2px 12px rgba(59, 35, 90, 0.08)"
      borderWidth="1px"
      borderColor="gray.100"
      h={{ md: '420px' }}
      minH={{ md: '380px' }}
    >
      <Box
        as="img"
        src={panelImage}
        alt=""
        w="100%"
        h={{ md: '200px' }}
        objectFit="cover"
      />
      <Box p={6}>
        <Text
          fontSize="sm"
          fontWeight="semibold"
          color={CIL.plum}
          mb={2}
          textTransform="uppercase"
          letterSpacing="wider"
        >
          {step.time}
        </Text>
        <Heading as="h3" size="md" color={CIL.plum} mb={4}>
          {step.title}
        </Heading>
        <Text color="gray.700" lineHeight="tall">
          {step.detail}
        </Text>
      </Box>
    </Box>
  );
};

/** Voce mobile: titolo cliccabile + contenuto in Collapse */
const MobileTimelineItem = ({ step, imageIndex, isOpen, onToggle }) => {
  const panelImage = step.sectionImageKey
    ? STEP_IMAGES[step.sectionImageKey]
    : DAY_IMAGES[imageIndex % DAY_IMAGES.length];
  return (
    <ListItem
      listStyleType="none"
      borderBottomWidth="1px"
      borderColor="gray.200"
      _last={{ borderBottom: 'none' }}
    >
      <Box
        as="button"
        type="button"
        w="100%"
        textAlign="left"
        py={3}
        px={4}
        display="flex"
        alignItems="baseline"
        gap={3}
        bg={isOpen ? CIL.plumLight : 'transparent'}
        _hover={{ bg: 'gray.50' }}
        _focus={{
          outline: '2px solid',
          outlineColor: CIL.plum,
          outlineOffset: '-2px',
        }}
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <Text fontSize="sm" fontWeight="600" color={CIL.plum} flexShrink={0}>
          {step.time}
        </Text>
        <Text fontSize="md" color="gray.800" fontWeight="500">
          {step.title}
        </Text>
      </Box>
      <Collapse in={isOpen}>
        <Box px={4} pb={4} pt={0} bg="gray.50">
          <Box
            as="img"
            src={panelImage}
            alt=""
            w="100%"
            h="160px"
            objectFit="cover"
            borderRadius="md"
            mt={3}
            mb={3}
          />
          <Text fontSize="sm" color="gray.700" lineHeight="tall">
            {step.detail}
          </Text>
        </Box>
      </Collapse>
    </ListItem>
  );
};

const AUTO_ADVANCE_MS = 3000;

// Sinistra: fino a e incluso "Canto". Destra: da "Pranzo" in poi.
const CANTO_INDEX = DAY_TIMELINE.findIndex((s) => s.title === 'Canto');
const LUNCH_SPLIT = CANTO_INDEX >= 0 ? CANTO_INDEX : 4;
const TIMELINE_BEFORE_LUNCH = DAY_TIMELINE.slice(0, LUNCH_SPLIT + 1);
const TIMELINE_AFTER_LUNCH = DAY_TIMELINE.slice(LUNCH_SPLIT + 1);

const DayTimelineSection = () => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const isDesktop = useBreakpointValue({ base: false, md: true });
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!isDesktop || isPaused) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return () => {};
    }
    intervalRef.current = setInterval(() => {
      setSelectedIndex((i) => (i + 1) % DAY_TIMELINE.length);
    }, AUTO_ADVANCE_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isDesktop, isPaused]);

  const selectedStep = selectedIndex >= 0 ? DAY_TIMELINE[selectedIndex] : null;

  return (
    <Box
      as="section"
      id="section-day"
      py={{ base: 10, md: 14 }}
      scrollMarginTop={{ base: '80px', md: '140px' }}
      aria-labelledby="giornata-tipo-heading"
    >
      <Box
        w="100%"
        maxW="100%"
        px={{ base: 4, md: 6, lg: 8 }}
        position="relative"
      >
        {/* Titolo sezione */}
        <Box
          id="giornata-tipo-heading"
          w="100%"
          mb={{ base: 6, md: 8 }}
          textAlign="center"
          px={4}
          py={3}
          borderRadius="lg"
          sx={{ background: SECTION_TITLE_CONTAINER_BG }}
          boxShadow="sm"
          overflow="hidden"
        >
          <Heading as="h2" size="lg" srOnly>
            Una giornata tipo al centro estivo CIL al Munlab
          </Heading>
          <Text
            as="span"
            aria-hidden="true"
            display={{ base: 'inline-block', md: 'none' }}
            fontSize="xl"
            fontWeight="bold"
            color={SECTION_TITLE_TEXT_COLOR}
          >
            Una giornata tipo
          </Text>
          <Text
            as="span"
            aria-hidden="true"
            display={{ base: 'none', md: 'inline-block' }}
            fontSize="2xl"
            fontWeight="bold"
            color={SECTION_TITLE_TEXT_COLOR}
          >
            Una giornata tipo al centro estivo CIL al Munlab
          </Text>
        </Box>

        {isDesktop ? (
          /* Desktop: 3 colonne – orari fino a pranzo | scheda | orari dopo pranzo */
          <Grid
            templateColumns="minmax(200px, 1fr) minmax(280px, 1.4fr) minmax(200px, 1fr)"
            gap={6}
            alignItems="stretch"
            w="100%"
            maxW="100%"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <Box
              pr={2}
              pl={3}
              py={3}
              bg="gray.50"
              borderRadius="xl"
              borderWidth="1px"
              borderColor="gray.100"
            >
              <List spacing={0} styleType="none" m={0} p={0}>
                {TIMELINE_BEFORE_LUNCH.map((step, i) => (
                  <StepTab
                    key={`${step.time}-${step.title}`}
                    step={step}
                    isSelected={selectedIndex === i}
                    onClick={() => setSelectedIndex(i)}
                  />
                ))}
              </List>
            </Box>
            <Fade
              key={selectedIndex}
              in
              transition={{
                enter: { duration: 0.35 },
                exit: { duration: 0.25 },
              }}
            >
              <DetailPanel
                step={selectedStep ?? DAY_TIMELINE[0]}
                imageIndex={selectedIndex >= 0 ? selectedIndex : 0}
              />
            </Fade>
            <Box
              pr={2}
              pl={3}
              py={3}
              bg="gray.50"
              borderRadius="xl"
              borderWidth="1px"
              borderColor="gray.100"
            >
              <List spacing={0} styleType="none" m={0} p={0}>
                {TIMELINE_AFTER_LUNCH.map((step, i) => {
                  const globalIndex = LUNCH_SPLIT + 1 + i;
                  return (
                    <StepTab
                      key={`${step.time}-${step.title}`}
                      step={step}
                      isSelected={selectedIndex === globalIndex}
                      onClick={() => setSelectedIndex(globalIndex)}
                    />
                  );
                })}
              </List>
            </Box>
          </Grid>
        ) : (
          /* Mobile: lista con titolo cliccabile e contenuto collassabile */
          <Box
            bg="white"
            borderRadius="lg"
            overflow="hidden"
            boxShadow="sm"
            borderWidth="1px"
            borderColor="gray.100"
          >
            <List spacing={0} styleType="none" m={0} p={0}>
              {DAY_TIMELINE.map((step, index) => (
                <MobileTimelineItem
                  key={`${step.time}-${step.title}`}
                  step={step}
                  imageIndex={index}
                  isOpen={selectedIndex === index}
                  onToggle={() =>
                    setSelectedIndex(selectedIndex === index ? -1 : index)
                  }
                />
              ))}
            </List>
          </Box>
        )}

        {/* 3 card sotto la giornata tipo: stile come il pannello centrale */}
        <SimpleGrid
          columns={{ base: 1, md: 3 }}
          gap={6}
          mt={{ base: 8, md: 10 }}
          w="100%"
          maxW="100%"
        >
          {GIORNATA_TIPO_CARDS.map((card) => (
            <Box
              key={card.title}
              bg="white"
              borderRadius="lg"
              overflow="hidden"
              boxShadow="0 4px 20px rgba(0, 0, 0, 0.12), 0 8px 32px rgba(0, 0, 0, 0.08)"
              borderWidth="1px"
              borderColor="gray.100"
              p={6}
            >
              <Heading as="h3" size="md" color={CIL.plum} mb={3}>
                {card.title}
              </Heading>
              <Text color="gray.700" lineHeight="tall" fontSize="sm">
                {card.detail}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      </Box>
    </Box>
  );
};

export default DayTimelineSection;
